import { defineComponent as _defineComponent } from 'vue'
import '@/scss/parts/search/global-search.scss';
import { AiChatMessage } from '@/@types/AiChatMessage';
import { AiChatRole } from '@/@types/AiChatRole';
import { SearchCategory } from '@/@types/enums/SearchCategory';
import { SearchArticleTags } from '@/@types/SearchArticleTags';
import { SearchMode } from '@/@types/SearchMode';

import GlobalSearchSuggestionList from '@/components/layout/inputs/GlobalSearch/GlobalSearchSuggestionList.vue';
import SearchModeTag from '@/components/layout/inputs/GlobalSearch/SearchModeTag/SearchModeTag.vue';
import { PERSONAL_WORKSPACE_TITLE } from '@/constants/SharedConsts';
import EventBus from '@/EventBus';
import { TRIGGER_SEARCH_INPUT } from '@/events';
import helper from '@/helper';
import { Article } from '@/models/article/Article';
import { TextTab } from '@/models/article/TextTab';
import { SearchByTextProperty } from '@/models/SearchByText';
import { Workspace } from '@/models/Workspace';
import { secondsToTime } from '@/other/helpers/timeHelpers';
import apiService from '@/services/ApiService';
import { workspaceService } from '@/services/workspace/WorkspaceService';

import store from '@/store';
import { debounce } from 'lodash';

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalSearch',
  setup(__props) {

const QUERY_LIMIT = 500;

const suggestions = ref<Record<string, any>[]>([]);
const aiSuggestions = ref<AiChatMessage[]>([]);
const relatedAiSources = ref<Record<string, any>[]>([]);
const searchHistory = ref<Record<string, any>>({});

const searchMode = ref<SearchMode | null>(null);

const isAiSuggestionsLoading = ref(false);
const isSuggestionsLoading = ref(false);
const isSearchHistoryLoading = ref(false);

const searchState = ref<string>('');
const searchQuery = ref<string>('');
const triggerInput = ref(false);
const search = ref<string>('');
const hasBottomReached = ref(false);
const isSuggestionsOpened = ref(false);
const currentPage = ref<number>(1);
const currentSearchCategory = ref<SearchCategory>(SearchCategory.ALL);

let clickedElement: any = null;

const PAGE_LIMIT = 15;

const routes = useRoute();

const currentRouteName = computed((): string => routes.name);

const isHome = computed(() => currentRouteName.value === 'Home');

const searchPlaceholder = computed(() => {
  switch (searchMode.value) {
    case SearchMode.AI:
      return aiSuggestions.value.length ? 'Ask anything else?' : 'Ask a question';
    default:
      return 'Search';
  }
});

const isSuggestionsEmpty = computed(() => {
  return !suggestions.value.length && !aiSuggestions.value.length;
});

const enableAiSearchMode = async () => {
  searchMode.value = SearchMode.AI;
  suggestions.value = [];
  aiSuggestions.value = [];

  if (searchState?.value?.length) {
    await handleAiSearchInput();
    return;
  }

  isSuggestionsOpened.value = false;

  searchQuery.value = '';
  searchState.value = '';

  return;
};

const handleRemoveSearchMode = () => {
  searchMode.value = null;
  aiSuggestions.value = [];
  relatedAiSources.value = [];

  if (searchState.value.length) {
    if (!suggestions.value.length) {
      loadSearchSuggestions();
    }
  }
};

const handleHasBottomReachedChange = (value: boolean) => (hasBottomReached.value = value);

const handleTriggerQuery = async () => {
  // Check if it`s an initial query or not
  if (searchMode.value === SearchMode.AI) return;
  if (currentSearchCategory.value === SearchCategory.ALL) {
    const lastItemCategory: SearchCategory = suggestions.value[suggestions.value.length - 1]?.category
      ? suggestions.value[suggestions.value.length - 1]?.category
      : SearchCategory.NONE;
    currentSearchCategory.value = lastItemCategory;
    const numberOfLastItemCategory = suggestions.value.filter((item) => item.category === lastItemCategory).length;
    const res = await querySearch(searchState.value, currentPage.value, lastItemCategory, numberOfLastItemCategory);
    if (res.results) suggestions.value = [...suggestions.value, ...res.results];
  } else {
    const res = await querySearch(searchState.value, currentPage.value, currentSearchCategory.value);

    if (res.results) suggestions.value = [...suggestions.value, ...res.results];
  }
};

const isResultMatch = (itemValue: any) => {
  const regExpr = /[$^*()+{}[\]?|\\.]/g;
  let match: RegExpExecArray | null;
  const matches = [];
  search.value = searchQuery.value;
  const newSearchQuery = searchQuery.value.split('');
  while ((match = regExpr.exec(newSearchQuery.join(''))) !== null) {
    matches.push(match);
  }
  if (regExpr.test(searchQuery.value)) {
    for (let y = 0; y < matches.length; y++) {
      newSearchQuery.splice(matches[y].index + y, 0, `\\`);
    }
    search.value = newSearchQuery.join('');
  }

  return itemValue?.toLowerCase().match(search?.value?.toLowerCase());
};

const resetSearch = () => {
  isSuggestionsOpened.value = false;
  searchState.value = '';
  suggestions.value = [];
  searchMode.value = null;
  triggerInput.value = false;
};

const filterItems = (array: any[]): any[] => array.filter((item: any) => isResultMatch(item.title));

const getWorkspaceName = (wsId: string): string => {
  const ws = store.state.workspaces.find((ws: Workspace) => ws.id === wsId);
  return workspaceService.getWorkspaceTitle(ws);
};

const getWorkspaceSlug = (wsId: string): string => {
  const ws = store.state.workspaces.find((ws: Workspace) => ws.id === wsId);
  return ws ? ws.slug : '';
};

const getNotesContent = (articles: any, searchQuery: string, category: SearchCategory): any[] => {
  let articlesContent: any[] = [];
  articles.forEach((a: any) => {
    if (!a.textTabs) return articlesContent;
    a.textTabs.forEach((tab: TextTab) => {
      const strippedText = helper.stripHtml(tab.text);
      const strippedMatches = helper.searchAllInTextWithoutTags(strippedText, searchQuery, 3);
      const htmlMatches = helper.searchAllInText(tab.text, searchQuery);
      const resultItem: any = [];
      strippedMatches.forEach((matchedObject: any, index: number) => {
        if (htmlMatches[index]) {
          const trimmedText = helper.trimContentText(matchedObject, strippedText);
          const wsName = getWorkspaceName(a.workspace);
          const splitSearchQueries = searchQuery.split(' ');
          const foundTextProperties: SearchByTextProperty[] = [];
          splitSearchQueries.forEach((word, queryIndex) => {
            if (word) {
              const keyWord = word?.toLowerCase();
              const matchesText = `${htmlMatches[index][0]}`?.toLowerCase();
              const prevKeyWordIndex =
                foundTextProperties.length && foundTextProperties[queryIndex - 1]
                  ? foundTextProperties[queryIndex - 1].initialIndex + foundTextProperties[queryIndex - 1].queryLength
                  : 0;

              const initialIndex = matchesText.indexOf(keyWord);
              if (Number.isInteger(initialIndex) && initialIndex >= 0) {
                const startIndex = !queryIndex
                  ? htmlMatches[index].index
                  : htmlMatches[index].index + matchesText.indexOf(keyWord, prevKeyWordIndex);
                const queryLength = keyWord.trim().length;
                const textToFind = `${htmlMatches[index][0]}`.slice(initialIndex, initialIndex + queryLength);

                const result = {
                  initialIndex,
                  startIndex,
                  queryLength,
                  textToFind,
                  textTabId: tab.id,
                };

                foundTextProperties.push(result);
              }
            }
          });

          const resObject = {
            ...a,
            parentArticle: `  ${a.title} `,
            ...(!(a.draft || a.original)
              ? {
                  wsName: getWorkspaceName(a.workspace),
                  wsSlug: getWorkspaceSlug(a.workspace),
                  parentWorkspace: `  in ${wsName}`,
                }
              : {}),
            textTab: tab,
            category,
            foundTextProperties,
            trimmedText,
          };

          resultItem.push(resObject);
        }
      });
      articlesContent = [...articlesContent, ...resultItem];
    });
  });
  return articlesContent;
};

const paragraphsFilter = (article: Article): any[] => {
  const results: any[] = [];
  const model = Article.fromJson(article);

  model.eachParagraph((item: any) => {
    let res = isResultMatch(item.title);

    if (!res && item.tags) {
      for (const tag of item.tags) {
        if (isResultMatch(tag)) {
          res = true;
          break;
        }
      }
    }

    if (res) {
      results.push(item);
    }
  });
  return results;
};

const getParagraphs = (articles: any): any[] => {
  let paragraphs: any[] = [];
  articles.forEach((a: any) => {
    if (a.paragraphs.length !== 0) {
      let items = paragraphsFilter(a);

      // add article link to paragraphs
      if (items && items.length) {
        items = items.map((i: any) => {
          i.article = a;
          i.parentArticle = `  ${a.title} `;
          const wsName = getWorkspaceName(a.workspace);
          i.parentWorkspace = `  in ${wsName}`;
          return i;
        });
      }
      paragraphs = [...paragraphs, ...items];
    }
  });
  return paragraphs;
};

const getArticlesTags = (articles: any): SearchArticleTags[] => {
  const resTags: SearchArticleTags[] = [];
  articles.forEach((article: Article) => {
    article.tags.forEach((tag: string) => {
      if (!tag.includes(searchQuery.value)) return;
      const trimmedText = tag.length > 35 ? helper.trimContentText(searchQuery.value, tag) : tag;
      const resTag = {
        ...article,
        trimmedText,
        parentArticle: ` in ${article.title}`,
        category: SearchCategory.TAG,
      };
      resTags.push(resTag);
    });
  });
  return resTags;
};

const getArticleTranscriptionText = (articles: any, searchQuery: string, category: SearchCategory): any[] => {
  let articlesContent: any[] = [];

  articles.forEach((a: any) => {
    const plainVideoTranscription = a.transcription[0].plainTranscription;
    const videoTranscriptionWithTime = JSON.parse(a.transcription[0].transcription);
    const strippedMatches: RegExpExecArray[] = helper.searchAllInTextWithoutTags(
      plainVideoTranscription,
      searchQuery,
      3
    );
    const resultItem: any = [];

    strippedMatches.forEach((matchedObject) => {
      const trimmedText = helper.trimContentText(matchedObject, plainVideoTranscription);
      const wsName = getWorkspaceName(a.workspace);
      const splitSearchQueries = searchQuery.split(' ');
      const foundTextProperties: any[] = [];
      let startTime = 0;

      const flattenedTranscription = ([] as any[]).concat(...videoTranscriptionWithTime);

      const searchedSubtitleItems = flattenedTranscription.filter((item) => {
        const isSubtitleExist = item.text && searchQuery && item.text.toLowerCase().includes(searchQuery.toLowerCase());
        const allStartTimes = resultItem.map((result: any) => result.startTime / 1000);
        const currentSubtitleTime = item.start;
        return isSubtitleExist && !allStartTimes.includes(currentSubtitleTime);
      });

      if (searchedSubtitleItems.length) startTime = searchedSubtitleItems[0].start;

      splitSearchQueries.forEach((word) => {
        if (word) {
          const keyWord = word?.toLowerCase();
          const queryLength = keyWord.trim().length;

          const result = {
            queryLength,
            textToFind: keyWord,
          };
          foundTextProperties.push(result);
        }
      });

      const resObject = {
        ...a,
        parentArticle: `  ${a.title} `,
        wsName: getWorkspaceName(a.workspace),
        wsSlug: getWorkspaceSlug(a.workspace),
        parentWorkspace: `  in ${wsName}`,
        category,
        foundTextProperties,
        trimmedText,
        saidAt: secondsToTime(startTime),
        startTime: startTime * 1000,
      };

      resultItem.push(resObject);
    });

    articlesContent = [...articlesContent, ...resultItem];
  });

  return articlesContent;
};

const formatWorkspaces = (workspaces: Workspace[]) => {
  return filterItems(workspaces).map((workspace: Workspace) => {
    const workspaceTitle = workspace.title?.indexOf('PW_') == 0 ? PERSONAL_WORKSPACE_TITLE : workspace.title;
    return {
      ...workspace,
      title: workspaceTitle,
      category: SearchCategory.WORKSPACE,
    };
  });
};

const formatArticles = (articles: Article[]) => {
  return filterItems(articles).map((article: Article) => {
    const workspaceId = typeof article.workspace === 'string' ? article.workspace : article.workspace.id;
    const wsName = getWorkspaceName(workspaceId);
    return { ...article, category: SearchCategory.ARTICLE, parentWorkspace: ` in ${wsName}` };
  });
};

const formatDrafts = (drafts: Article[]) => {
  return filterItems(drafts).map((draft: Article) => {
    const workspaceId = !draft.workspace
      ? null
      : typeof draft.workspace === 'string'
      ? draft.workspace
      : draft.workspace.id;

    const wsName = workspaceId ? getWorkspaceName(workspaceId) : 'Drafts';
    return { ...draft, category: SearchCategory.DRAFT, parentWorkspace: ` in ${wsName ? wsName : 'Drafts'}` };
  });
};

const formatAllResults = (results: any, q: string): any[] => {
  const workspaces = results[SearchCategory.WORKSPACE].map((workspace: Workspace) => Workspace.parse(workspace));
  const articles = results[SearchCategory.ARTICLE].map((article: Article) => Article.fromJson(article));
  const drafts = results[SearchCategory.DRAFT].map((draft: Article) => draft);
  const paragraphs = getParagraphs(results[SearchCategory.PARAGRAPH]);

  const resWorkspaces = formatWorkspaces(workspaces);
  const resArticles = formatArticles(articles);
  const resArticleNotes = getNotesContent(results[SearchCategory.ARTICLE_NOTES], q, SearchCategory.ARTICLE_NOTES);
  const resDrafts = formatDrafts(drafts);
  const resDraftNotes = getNotesContent(results[SearchCategory.DRAFT_NOTES], q, SearchCategory.DRAFT_NOTES);
  const resParagraphs = paragraphs.map((paragraph) => ({ ...paragraph, category: SearchCategory.PARAGRAPH }));
  const resTags = getArticlesTags(results[SearchCategory.TAG]);

  const resArticleTranscription = getArticleTranscriptionText(
    results[SearchCategory.ARTICLE_VIDEO],
    q,
    SearchCategory.ARTICLE_VIDEO
  );

  const resDraftTranscription = getArticleTranscriptionText(
    results[SearchCategory.DRAFT_VIDEO],
    q,
    SearchCategory.DRAFT_VIDEO
  );

  // gather all results and return only first 15
  let finalResult = [
    ...resWorkspaces,
    ...resArticles,
    ...resArticleNotes,
    ...resDrafts,
    ...resDraftNotes,
    ...resDraftTranscription,
    ...resParagraphs,
    ...resTags,
    ...resArticleTranscription,
  ];

  return finalResult.slice(0, PAGE_LIMIT);
};

const querySearch = async (q: string, page: number, category: SearchCategory, skip?: number): Promise<any> => {
  if (!q || category === SearchCategory.NONE || q === '.' || q === '..') {
    return Promise.resolve([]);
  }

  searchQuery.value = q;

  try {
    const res: any = await apiService.globalSearch.search(q, page, category, skip);

    if (!res.data) return Promise.resolve([]);
    let results: any = [];
    // process result for corresponding search category to display them in list
    switch (category) {
      case SearchCategory.ALL:
        results = formatAllResults(res.data.results, q);
        break;
      case SearchCategory.WORKSPACE:
        results = formatWorkspaces(res.data.results);
        updateSearchAndPage(results.length, SearchCategory.ARTICLE);
        break;
      case SearchCategory.ARTICLE:
        results = formatArticles(res.data.results);
        updateSearchAndPage(results.length, SearchCategory.DRAFT);
        break;
      case SearchCategory.DRAFT:
        results = formatDrafts(res.data.results);
        updateSearchAndPage(results.length, SearchCategory.PARAGRAPH);
        break;
      case SearchCategory.PARAGRAPH:
        results = getParagraphs(res.data.results).map((paragraph) => {
          return { ...paragraph, category: SearchCategory.PARAGRAPH };
        });
        updateSearchAndPage(results.length, SearchCategory.TAG);
        break;
      case SearchCategory.TAG:
        results = getArticlesTags(res.data.results);
        updateSearchAndPage(results.length, SearchCategory.DRAFT_NOTES);
        break;
      case SearchCategory.DRAFT_NOTES:
        results = getNotesContent(res.data.results, q, SearchCategory.DRAFT_NOTES);
        updateSearchAndPage(results.length, SearchCategory.ARTICLE_NOTES);
        break;
      case SearchCategory.ARTICLE_NOTES:
        results = getNotesContent(
          res.data.results.map((article: Article) => Article.fromJson(article)),
          q,
          SearchCategory.ARTICLE_NOTES
        );
        updateSearchAndPage(results.length, SearchCategory.ARTICLE_VIDEO);
        break;
      case SearchCategory.ARTICLE_VIDEO:
        results = getArticleTranscriptionText(
          res.data.results.map((article: any) => article),
          q,
          SearchCategory.ARTICLE_VIDEO
        );
        updateSearchAndPage(results.length, SearchCategory.DRAFT_VIDEO);
        break;
      case SearchCategory.DRAFT_VIDEO:
        results = getArticleTranscriptionText(
          res.data.results.map((article: any) => article),
          q,
          SearchCategory.DRAFT_VIDEO
        );
        updateSearchAndPage(results.length, SearchCategory.NONE);
        break;
    }

    return Promise.resolve({ results });
  } catch (e) {
    apiService.handleResponseError(e);
    return { results: [] };
  }
};

const updateSearchAndPage = (length: number, category: SearchCategory) => {
  if (!(length < PAGE_LIMIT || !length)) return (currentPage.value = currentPage.value + 1);

  currentSearchCategory.value = category;
  currentPage.value = 1;
};

const handleInput = debounce(async () => {
  await Promise.all([loadSearchSuggestions()]);
}, 500);

const loadSearchHistory = async () => {
  try {
    isSearchHistoryLoading.value = true;
    const res = await apiService.globalSearch.getSearchHistory();

    if (!res.data?.history?.questions?.length) return;
    searchHistory.value = res.data.history;
  } catch (e) {
    apiService.handleResponseError(e);
  } finally {
    isSearchHistoryLoading.value = false;
  }
};

const loadSearchSuggestions = async () => {
  suggestions.value = [];

  if (!searchState.value.length || searchMode.value === SearchMode.AI) return;

  isSuggestionsOpened.value = true;
  if (!isHome.value) triggerInput.value = true;
  isSuggestionsLoading.value = true;
  hasBottomReached.value = false;
  currentPage.value = 1;
  currentSearchCategory.value = SearchCategory.ALL;

  try {
    const res = await querySearch(searchState.value, 1, SearchCategory.ALL);
    if (res.results && res.results.length && searchState.value.length) {
      if (!isSuggestionsOpened.value) isSuggestionsOpened.value = true;

      suggestions.value = res.results;
    }
  } finally {
    setTimeout(() => {
      isSuggestionsLoading.value = false;
    }, 150);
  }
};

const handleAiSearchInput = async () => {
  if (searchMode.value !== SearchMode.AI) {
    await enableAiSearchMode();
  }

  if (!searchState.value.trim() || isAiSuggestionsLoading.value) return;

  isSuggestionsOpened.value = true;
  isAiSuggestionsLoading.value = true;

  try {
    const userQuery = structuredClone(searchState.value);
    searchState.value = '';

    const messagesLimit = 6;
    let skipNextMessage = false;

    const previousMessages = [...(aiSuggestions.value || [])]
      .map((m, index, arr) => {
        // Check if the current message is from the USER
        if (m.role === AiChatRole.USER) {
          const nextMessage = arr[index + 1];

          // If the next message is from the SYSTEM and has no context, skip it
          if (nextMessage?.role === AiChatRole.DEVELOPER && !nextMessage?.context?.length) {
            skipNextMessage = true;
            return null;
          }

          // If the next message is also from the USER, skip the current and next message
          if (nextMessage?.role === AiChatRole.USER) {
            skipNextMessage = true;
            return null;
          }
        }

        // If skipNextMessage flag is true, skip the current message
        if (skipNextMessage) return null;

        skipNextMessage = false;
        return m;
      })
      .filter(Boolean) // Remove any null values (skipped messages)
      .slice(-messagesLimit); // Limit the number of messages

    aiSuggestions.value.push({ role: AiChatRole.USER, content: userQuery });

    const aiRes = await apiService.globalSearch.aiSearch(userQuery, previousMessages);

    if (aiRes.data) {
      aiSuggestions.value.push(aiRes.data);

      const sources = aiRes.data.metadata?.sources;
      if (sources) {
        for (const source of sources) {
          const isDraft = !!source?.isDraft;
          const idField = isDraft ? 'draftId' : 'articleId';

          const exists = relatedAiSources.value.some((item) => item[idField] === source[idField]);

          if (!exists) {
            relatedAiSources.value.unshift(source);
          }
        }
      }
    }
  } finally {
    isAiSuggestionsLoading.value = false;

    if (isSuggestionsEmpty.value && searchMode.value === SearchMode.AI) isSuggestionsOpened.value = false;
  }
};

const handleInputFocus = async () => {
  if (!isHome.value) triggerInput.value = true;

  await loadSearchHistory();

  const hasContent = suggestions.value.length || aiSuggestions.value.length || searchHistory.value?.questions?.length;

  if (hasContent && (!searchMode.value || (searchMode.value === SearchMode.AI && aiSuggestions.value.length))) {
    isSuggestionsOpened.value = true;
  }
};

const handleFocusOut = () => {
  if (
    !clickedElement ||
    clickedElement.closest('.global-search__suggestion') ||
    clickedElement.closest('.global-search__input-element') ||
    clickedElement.closest('.input-hint__ai-btn')
  )
    return;

  if (!isSuggestionsOpened) return;
  isSuggestionsOpened.value = false;

  if (!isHome.value) resetSearch();
};

const handleDocumentClick = (event: MouseEvent) => {
  clickedElement = event.target as HTMLElement;

  handleFocusOut();
};

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleDocumentClick);
});

watch(
  () => triggerInput.value,
  () => {
    EventBus.$emit(TRIGGER_SEARCH_INPUT, triggerInput.value);
  }
);

watch(
  () => searchState.value,
  () => {
    if (!searchState.value.length) suggestions.value = [];
  }
);

return { __sfc: true,QUERY_LIMIT, suggestions, aiSuggestions, relatedAiSources, searchHistory, searchMode, isAiSuggestionsLoading, isSuggestionsLoading, isSearchHistoryLoading, searchState, searchQuery, triggerInput, search, hasBottomReached, isSuggestionsOpened, currentPage, currentSearchCategory, clickedElement, PAGE_LIMIT, routes, currentRouteName, isHome, searchPlaceholder, isSuggestionsEmpty, enableAiSearchMode, handleRemoveSearchMode, handleHasBottomReachedChange, handleTriggerQuery, isResultMatch, resetSearch, filterItems, getWorkspaceName, getWorkspaceSlug, getNotesContent, paragraphsFilter, getParagraphs, getArticlesTags, getArticleTranscriptionText, formatWorkspaces, formatArticles, formatDrafts, formatAllResults, querySearch, updateSearchAndPage, handleInput, loadSearchHistory, loadSearchSuggestions, handleAiSearchInput, handleInputFocus, handleFocusOut, handleDocumentClick, GlobalSearchSuggestionList, SearchModeTag }
}

})